/**
 * feat-107 Slice A — right pane: Question detail editor (create + edit).
 *
 * Type segmented control, question text, A–D options with a correct-answer radio
 * (MC) plus an accepted-variants textarea (Fill), XP, wrong-answer explanation,
 * right-answer praise, and the shared pinned footer.
 *
 * feat-109: in create mode the Type control is the shared 3-way DetailTypeControl
 * (MC / Fill / Rehearsal) — picking Rehearsal calls `onRequestRehearsal` so the
 * parent swaps to the rehearsal form.
 *
 * feat-259 — THIS PANEL NO LONGER HAS A SAVE BUTTON, OR A CANCEL BUTTON.
 * It auto-saves: text fields on blur, non-text controls on change, and the
 * FIRST blur in create mode creates the row. `useAutoSave` owns all of it; this
 * file only says which field is which kind.
 *
 * The header comment this replaces said the save "ALWAYS includes the
 * translatable fields … so feat-018 retranslation fires". That was deliberate
 * and it is deliberately gone: `_touches_translatable`
 * (`routes/admin_quiz_content.py:58`) fires a Spanish retranslate whenever a
 * translatable field is PRESENT in the body, so under per-blur auto-save the old
 * always-send-everything body would enqueue a full retranslate on every blur.
 * Sending only the changed fields preserves retranslate-when-the-text-changed
 * and drops it for an XP or sort edit — spec §Translation, AC1/AC8, and the
 * backend slice asserts the other half of it.
 */

import React from 'react';
import {
  createQuestion,
  updateQuestion,
  deleteQuestion,
  CorrectOption,
  QuestionType,
  QuizQuestion,
  BlankGradingMode,
} from '../../../services/quizContentAdminService';
import { QUIZ_TERMS, QUESTION_TYPE_BADGE } from '../quizTerminology';
import { DetailTypeControl } from './DetailTypeControl';
import ContentTimestamps from './ContentTimestamps';
import { useAutoSave, PanelSaveWiring } from './useAutoSave';
import { ItemDetailFooter, SaveErrorBanner } from './ItemDetailFooter';
import {
  ApprovedFieldMarker,
  ApprovedValueLine,
  PanelApprovalState,
} from './approval/itemApproval';
import {
  BLANK_GRADING_COPY,
  GRADING_MODE_CHOICES,
  fromGradingModeChoice,
  toGradingModeChoice,
} from './blankGradingCopy';
import { TryBlankAnswer } from './TryBlankAnswer';
import { AUTO_SAVE_COPY } from './autoSaveCopy';
import {
  mcOptionsProblem,
  isMcOptionLocked,
  mcOptionPlaceholder,
  blanksCorrectOption,
  isOptionFilled,
  MC_CORRECT_BLANK_MESSAGE,
} from '../../../utils/mcOptions';

const OPTION_LETTERS: CorrectOption[] = ['a', 'b', 'c', 'd'];

export interface QuestionDetailFormProps extends PanelSaveWiring {
  /** null = create mode. */
  question: QuizQuestion | null;
  lessonId: string;
  defaultSort: number;
  /** Fired ONCE, when the first blur creates the row. The shell reloads and
   *  selects it — a create genuinely changes the item list, so unlike an
   *  auto-save it still reloads (AT10). */
  onCreated: (savedId: string) => void;
  onDelete?: () => void;
  /** feat-109: starting question type for a NEW question (create mode). */
  initialType?: QuestionType;
  /** feat-109: present only in create mode → renders the multi-way Type control;
   *  picking Rehearsal calls this so the parent swaps to the rehearsal form. */
  onRequestRehearsal?: () => void;
  /** feat-165: present only in create mode → picking Video calls this so the
   *  parent swaps to the video detail form. */
  onRequestVideo?: () => void;
  /** feat-166: present only in create mode → picking Reading calls this so the
   *  parent swaps to the reading detail form. */
  onRequestReading?: () => void;
}

type Draft = {
  question_type: QuestionType;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  /** feat-346: null = no pick. Blanking the picked MC option clears it, and the
   *  option set is held back (never sent) until the author picks again. */
  correct_option: CorrectOption | null;
  explanation: string;
  correct_explanation: string;
  xp_value: number | null;
  /** null = "no variants" (what MC always sends). The textarea renders `?? ''`;
   *  a blur that leaves it blank NORMALISES BACK TO null so the field settles
   *  clean instead of re-sending `'' → null` on every subsequent blur. This is
   *  a wire-level nullable, NOT the `''`-vs-absent rule: `''` still means
   *  "cleared and must be sent" for every other field in this panel. */
  blank_accepted_variants: string | null;
  /** feat-325: AI grading override, fill-blank only. null = Auto (CONTRACT §2).
   *  Not approval-gated (CONTRACT r2), so no ApprovedFieldMarker. */
  blank_grading_mode: BlankGradingMode | null;
  sort_order: number;
};

/** ONE source for each field's on-screen label. The `<label>` the editor reads
 *  and the name the error banner uses are the same string by construction —
 *  design/error-states.html: "field names come from the label the editor is
 *  looking at", never a wire name like `question_text`. */
export const QUESTION_FIELD_LABELS: Record<keyof Draft, string> = {
  question_type: 'Type',
  question_text: `${QUIZ_TERMS.question.singular} text`,
  option_a: 'Option A',
  option_b: 'Option B',
  option_c: 'Option C',
  option_d: 'Option D',
  correct_option: 'Correct answer',
  explanation: 'Wrong-answer explanation',
  correct_explanation: 'Right-answer praise',
  xp_value: 'XP',
  blank_accepted_variants: 'Accepted answer variants',
  blank_grading_mode: BLANK_GRADING_COPY.label,
  sort_order: 'Sort order',
};

/** feat-346: the fields a multiple_choice option-set verdict covers. While the
 *  draft's set is invalid (A/B blank, a gap, a blank pick) the dirty ones are
 *  HELD — the backend validates post-merge and would 422 any of them alone. */
const MC_OPTION_SET_FIELDS: ReadonlyArray<keyof Draft> = [
  'question_type',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'correct_option',
];

const draftOptions = (d: Draft) => [d.option_a, d.option_b, d.option_c, d.option_d];

/** feat-346: null when the draft's options are fine to send. Fill-blank is never
 *  checked (its rules are unchanged — imported rows carry B–D ''). */
export function questionOptionsProblem(d: Draft): string | null {
  if (d.question_type !== 'multiple_choice') return null;
  return mcOptionsProblem(draftOptions(d), d.correct_option)?.message ?? null;
}

function toDraft(q: QuizQuestion | null, defaultSort: number, initialType: QuestionType): Draft {
  if (!q) {
    return {
      question_type: initialType,
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_option: 'a',
      explanation: '',
      correct_explanation: '',
      xp_value: null,
      blank_accepted_variants: null,
      blank_grading_mode: null,
      sort_order: defaultSort,
    };
  }
  return {
    question_type: q.question_type,
    question_text: q.question_text,
    option_a: q.option_a,
    option_b: q.option_b,
    option_c: q.option_c,
    option_d: q.option_d,
    correct_option: q.correct_option,
    explanation: q.explanation,
    correct_explanation: q.correct_explanation,
    xp_value: q.xp_value,
    blank_accepted_variants: q.blank_accepted_variants ?? null,
    blank_grading_mode: q.blank_grading_mode ?? null,
    sort_order: q.sort_order,
  };
}

export const QuestionDetailForm: React.FC<QuestionDetailFormProps> = ({
  question,
  lessonId,
  defaultSort,
  onCreated,
  onDelete,
  initialType = 'multiple_choice',
  onRequestRehearsal,
  onRequestVideo,
  onRequestReading,
  registerFlush,
  pendingNavigation = false,
  onNavigationProceed,
  onPatched,
}) => {
  const isCreate = question === null;

  // No `useEffect(() => setDraft(toDraft(question, …)))` any more, and its
  // absence is deliberate. Selection changes remount this panel via its `key`
  // (QuizContentManagement), so the effect's only remaining job would be to
  // overwrite the editor's live draft from a prop — which under auto-save is a
  // value thief and a focus thief. AT10 removes the reload that used to make
  // that prop change on every save in the first place.
  const save = useAutoSave<Draft>({
    initial: toDraft(question, defaultSort, initialType),
    itemId: question?.id ?? null,
    labels: QUESTION_FIELD_LABELS,
    create: async (draft) => {
      const { id } = await createQuestion(lessonId, {
        question_type: draft.question_type,
        question_text: draft.question_text,
        option_a: draft.option_a,
        option_b: draft.option_b,
        option_c: draft.option_c,
        option_d: draft.option_d,
        // feat-346: `createGate` guarantees a pick for MC; fill-blank keeps its default.
        correct_option: draft.correct_option ?? 'a',
        explanation: draft.explanation,
        correct_explanation: draft.correct_explanation,
        xp_value: draft.xp_value,
        blank_accepted_variants:
          draft.question_type === 'fill_blank' ? draft.blank_accepted_variants : null,
        blank_grading_mode:
          draft.question_type === 'fill_blank' ? draft.blank_grading_mode : null,
        sort_order: draft.sort_order,
      });
      return id;
    },
    // feat-346: `validateFields` holds a null `correct_option`, so it never reaches the wire.
    update: (id, changed) => updateQuestion(id, changed as Parameters<typeof updateQuestion>[1]),
    // feat-346: an MC row is not created until A + B exist and the pick is
    // filled — the backend 422s anything less. Nothing typed is lost.
    createGate: (d) => questionOptionsProblem(d),
    validateFields: (fields, d) => {
      const problem = questionOptionsProblem(d);
      if (!problem) {
        // A cleared pick is never sent, whatever the type (e.g. MC → Fill blank
        // straight after blanking the picked option).
        return d.correct_option === null && fields.includes('correct_option')
          ? [{ field: 'correct_option' as const, message: MC_CORRECT_BLANK_MESSAGE }]
          : [];
      }
      return fields
        .filter((f) => MC_OPTION_SET_FIELDS.includes(f))
        .map((field) => ({ field, message: problem }));
    },
    onPersisted: ({ id, changed, created }) => {
      if (created) onCreated(id);
      else onPatched?.(id, changed as Record<string, unknown>);
    },
  });

  const { draft } = save;
  const isFill = draft.question_type === 'fill_blank';

  React.useEffect(() => {
    registerFlush?.(save.flush);
    return () => registerFlush?.(null);
  }, [registerFlush, save.flush]);

  const handleDelete = async () => {
    if (!question || !onDelete) return;
    if (!window.confirm(`Delete this ${QUIZ_TERMS.question.singular}?`)) return;
    await deleteQuestion(question.id);
    onDelete();
  };

  /** Text/number fields: edit locally, write on blur. */
  const text = <K extends keyof Draft>(field: K) => ({
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      save.setField(field, e.target.value as Draft[K]),
    onBlur: () => save.commitField(field as string & keyof Draft),
  });

  /** The failing field gains a hairline so it is findable when the form is
   *  scrolled past it (design/error-states.html state A). */
  const fieldBorder = (field: keyof Draft) =>
    save.error?.fields.includes(field as string & keyof Draft)
      ? (save.error.kind === 'validation' ? 'border-attention-line' : 'border-danger-line')
      : 'border-softBorder';

  const handleNavRetry = async () => {
    if (await save.flush()) onNavigationProceed?.();
  };

  return (
    // feat-231: this pane was ALREADY built as scroll-area + footer-outside-it.
    // The only thing it lacked was a bounded height to resolve against, which
    // `min-h-0` (cancel the grid item's content-based minimum) plus
    // `overflow-hidden` (clip, don't grow) now supply. This is the reference
    // shape the reading and video panes converge on (design/NOTES.md §3).
    <aside className="border-l border-softBorder bg-cream/40 flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
        {/* feat-252: the header row and the timestamp are wrapped together so
            they are ONE `space-y-4` child — the timestamp is therefore not a
            `space-y` sibling and its own `mt-0.5` governs the gap. This header
            row's trailing edge is taken by the type badge, so the timestamp
            drops beneath it. `question` is null in create mode ⇒ renders nothing. */}
        <div>
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">
              {isCreate ? 'New' : 'Edit'} {QUIZ_TERMS.question.singular}
            </h3>
            <span className={`text-[10px] font-bold rounded-md px-2 py-1 ${isFill ? 'bg-preview-light text-preview' : 'bg-blue-50 text-blue-700'}`}>
              {QUESTION_TYPE_BADGE[draft.question_type]}
            </span>
          </div>
          <ContentTimestamps variant="stacked" className="mt-0.5" created={question?.created} />
        </div>

        {/* feat-270 panels 4 / 9 — ONE panel-level block, never one per field.
            Six amber blocks in a 380px column is the wall autoSaveCopy.ts's
            "one banner, never a stack" already forbids. */}
        <PanelApprovalState />

        {/* type segmented control — multi-way in create mode (MC / Fill /
            Rehearsal / Video), 2-way in edit mode (cross-kind conversion of a
            saved question isn't a supported API op, so only MC↔Fill toggles there). */}
        {isCreate && onRequestRehearsal ? (
          <DetailTypeControl
            value={draft.question_type}
            onChange={(next) => {
              if (next === 'rehearsal') return onRequestRehearsal();
              if (next === 'video') return onRequestVideo?.();
              if (next === 'reading') return onRequestReading?.();
              // Create mode: nothing exists to save yet, and the type is part of
              // the create body — a local edit, not a write.
              return save.setField('question_type', next);
            }}
          />
        ) : (
          <div>
            <label className="block text-xs font-bold text-inkMuted mb-1.5">
              {QUESTION_FIELD_LABELS.question_type}
            </label>
            <div className="inline-flex bg-gray-100 rounded-full p-0.5 text-xs font-semibold">
              <button
                type="button"
                // AC2 — a non-text control writes on CHANGE, without waiting for
                // a blur. Switching to MC also clears the fill-only variants, as
                // the explicit Save used to; `blank_accepted_variants` is not on
                // the translatable allow-list, so this cannot enqueue a
                // retranslate. feat-325: the grading mode is cleared the same
                // way (the backend stores NULL for MC — CONTRACT r2); a field
                // that is already null is not re-sent.
                onClick={() =>
                  save.saveFields({
                    question_type: 'multiple_choice',
                    blank_accepted_variants: null,
                    blank_grading_mode: null,
                  })
                }
                className={`rounded-full px-3 py-1.5 ${!isFill ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'}`}
              >
                Multiple choice
              </button>
              <button
                type="button"
                onClick={() => save.saveField('question_type', 'fill_blank')}
                className={`rounded-full px-3 py-1.5 ${isFill ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'}`}
              >
                Fill blank
              </button>
            </div>
          </div>
        )}

        {/* question text */}
        <div>
          <label className="block text-xs font-bold text-inkMuted mb-1.5" htmlFor="question-text">
            {QUESTION_FIELD_LABELS.question_text}
            <ApprovedFieldMarker field="question_text" />
          </label>
          <textarea
            id="question-text"
            rows={3}
            value={draft.question_text}
            {...text('question_text')}
            placeholder={isFill ? 'Sentence with a ___ blank' : 'Question text'}
            className={`w-full text-sm bg-white border ${fieldBorder('question_text')} rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40`}
          />
          {/* spec §Incomplete items save anyway — this is an ADVISORY now. It
              describes what still needs doing; it gates nothing. */}
          {isFill && !draft.question_text.includes('___') && (
            <p className="text-[11px] text-attention-on mt-1">Fill-blank needs a <code>___</code> marker in the text.</p>
          )}
          {/* §10.4 — INSIDE the field's own <div>. As a direct child of the
              scroll area's `space-y-4` it would gain 16px of gap and read as a
              separate field; `space-y-4` at (0,3,0) silently outranks any
              `-mt-*` at (0,1,0), and nothing warns. */}
          <ApprovedValueLine field="question_text" />
        </div>

        {/* options + correct answer */}
        <div>
          <label className="block text-xs font-bold text-inkMuted mb-1.5">
            {isFill ? 'Answer + fallback options (mark the correct one)' : 'Options & correct answer'}
            {/* NOTES §5 — the rogue edit is a ONE-CHARACTER diff and must not read
                as a small one. This is the answer key. */}
            <ApprovedFieldMarker field="correct_option" />
            <ApprovedFieldMarker field="blank_accepted_variants" />
          </label>
          <div className="space-y-2">
            {OPTION_LETTERS.map((letter, i) => {
              const field = `option_${letter}` as 'option_a' | 'option_b' | 'option_c' | 'option_d';
              // feat-346: for MC the picker offers only FILLED options, C/D are
              // optional, and D is locked while C is blank. Fill-blank unchanged.
              const opts = draftOptions(draft);
              const filled = isOptionFilled(opts[i]);
              const correct = draft.correct_option === letter && (isFill || filled);
              const locked = !isFill && isMcOptionLocked(i, opts);
              return (
                <label
                  key={letter}
                  className={`flex items-center gap-2 bg-white rounded-xl px-2.5 py-2 border ${
                    save.error?.fields.includes(field)
                      ? 'border-danger-line'
                      : correct
                        ? 'border-success/50'
                        : 'border-softBorder'
                  }`}
                >
                  {/* AC2 — the correct-answer radio writes immediately. */}
                  <input
                    type="radio"
                    name="correct-option"
                    checked={correct}
                    disabled={!isFill && !filled}
                    onChange={() => save.saveField('correct_option', letter)}
                    className="accent-success"
                  />
                  <span className="text-xs font-bold text-inkMuted w-4 uppercase">{letter}</span>
                  <input
                    aria-label={QUESTION_FIELD_LABELS[field]}
                    value={draft[field]}
                    {...text(field)}
                    onChange={(e) => {
                      const value = e.target.value;
                      const ci = draft.correct_option ? OPTION_LETTERS.indexOf(draft.correct_option) : null;
                      // feat-346: blanking the PICKED MC option clears the pick.
                      if (!isFill && blanksCorrectOption(i, ci, draft[field], value)) {
                        save.setFields({ [field]: value, correct_option: null } as Partial<Draft>);
                      } else {
                        save.setField(field, value);
                      }
                    }}
                    disabled={locked}
                    placeholder={mcOptionPlaceholder(i, !isFill, opts)}
                    className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none"
                  />
                  {correct && <span className="text-success text-xs font-bold">✓</span>}
                </label>
              );
            })}
          </div>
        </div>

        {/* fill-blank accepted variants */}
        {isFill && (
          <div>
            <label className="block text-xs font-bold text-inkMuted mb-1.5" htmlFor="blank-variants">
              {QUESTION_FIELD_LABELS.blank_accepted_variants}
            </label>
            <textarea
              id="blank-variants"
              rows={2}
              value={draft.blank_accepted_variants ?? ''}
              onChange={(e) => save.setField('blank_accepted_variants', e.target.value)}
              onBlur={() => {
                // Settle blank back to null BEFORE committing, so the field can
                // reach a clean state. Without this the draft sits at `''` while
                // the baseline is `null` and every later blur re-sends it.
                const v = draft.blank_accepted_variants;
                const trimmed = (v ?? '').trim();
                const settled = trimmed === '' ? null : trimmed;
                if (settled !== v) save.setField('blank_accepted_variants', settled);
                save.commitField('blank_accepted_variants');
              }}
              placeholder="One per line. The correct option above is already accepted — add only extra spellings/synonyms."
              className={`w-full text-sm bg-white border ${fieldBorder('blank_accepted_variants')} rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40`}
            />
          </div>
        )}

        {/* feat-325 — AI grading mode (fill-blank only). A non-text control, so
            it writes on CHANGE (AC2); Auto is the stored null. In create mode it
            is a local edit that rides along in the create body, like Type. */}
        {isFill && (
          <div>
            <span id="grading-mode-label" className="block text-xs font-bold text-inkMuted mb-1.5">
              {QUESTION_FIELD_LABELS.blank_grading_mode}
            </span>
            <div
              role="radiogroup"
              aria-labelledby="grading-mode-label"
              aria-describedby="grading-mode-help"
              className="inline-flex bg-gray-100 rounded-full p-0.5 text-xs font-semibold"
            >
              {GRADING_MODE_CHOICES.map((choice) => {
                const selected = toGradingModeChoice(draft.blank_grading_mode) === choice;
                return (
                  <button
                    key={choice}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      const value = fromGradingModeChoice(choice);
                      if (isCreate) save.setField('blank_grading_mode', value);
                      else save.saveField('blank_grading_mode', value);
                    }}
                    className={`rounded-full px-3 py-1.5 ${selected ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'}`}
                  >
                    {BLANK_GRADING_COPY.modeName[choice]}
                  </button>
                );
              })}
            </div>
            <p id="grading-mode-help" className="text-[11px] text-inkMuted mt-1">
              {BLANK_GRADING_COPY.help[toGradingModeChoice(draft.blank_grading_mode)]}
            </p>
          </div>
        )}

        {/* feat-325 — Try an answer. NOT part of the auto-save draft. */}
        {isFill && (
          <TryBlankAnswer
            questionId={save.itemId ?? question?.id ?? null}
            mode={draft.blank_grading_mode}
            flush={save.flush}
          />
        )}

        {/* XP */}
        <div>
          <label className="block text-xs font-bold text-inkMuted mb-1.5" htmlFor="question-xp">
            {QUESTION_FIELD_LABELS.xp_value}
          </label>
          <input
            id="question-xp"
            type="number"
            value={draft.xp_value ?? ''}
            onChange={(e) =>
              save.setField('xp_value', e.target.value === '' ? null : parseInt(e.target.value, 10) || 0)
            }
            onBlur={() => save.commitField('xp_value')}
            placeholder="Default for type"
            className={`w-full text-sm bg-white border ${fieldBorder('xp_value')} rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40`}
          />
        </div>

        {/* wrong-answer explanation */}
        <div>
          <label className="block text-xs font-bold text-inkMuted mb-1.5" htmlFor="question-explanation">
            {QUESTION_FIELD_LABELS.explanation}
            <ApprovedFieldMarker field="explanation" />
          </label>
          <textarea
            id="question-explanation"
            rows={2}
            value={draft.explanation}
            {...text('explanation')}
            placeholder="What to do differently, stated warmly."
            className={`w-full text-sm bg-white border ${fieldBorder('explanation')} rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40`}
          />
          <ApprovedValueLine field="explanation" />
        </div>

        {/* right-answer praise */}
        <div>
          <label className="block text-xs font-bold text-inkMuted mb-1.5" htmlFor="question-praise">
            {QUESTION_FIELD_LABELS.correct_explanation}
            <ApprovedFieldMarker field="correct_explanation" />
          </label>
          <textarea
            id="question-praise"
            rows={2}
            value={draft.correct_explanation}
            {...text('correct_explanation')}
            placeholder="Celebrate in one sentence."
            className={`w-full text-sm bg-white border ${fieldBorder('correct_explanation')} rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40`}
          />
          <ApprovedValueLine field="correct_explanation" />
        </div>
      </div>

      {/* Banner and footer are `shrink-0` SIBLINGS of the scroll area above:
          growth eats the field list, never the footer. */}
      <SaveErrorBanner
        error={save.error as never}
        blockedReason={save.blockedReason}
        blockedDetail={save.itemId === null ? AUTO_SAVE_COPY.createNothingSaved : null}
        pendingNavigation={pendingNavigation}
        typeSingular={QUIZ_TERMS.question.singular}
        onRetry={pendingNavigation ? handleNavRetry : save.retry}
        onLeaveAnyway={onNavigationProceed}
      />
      <ItemDetailFooter
        onDelete={!isCreate && onDelete ? handleDelete : undefined}
        status={save.status}
        savedAt={save.savedAt}
      />
    </aside>
  );
};

export default QuestionDetailForm;
